import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram, Youtube } from "lucide-react";
import HUD from "@/components/HUD";
import { useConfig } from "@/context/ConfigContext";

const NAV = [
  { to: "/", label: "INDEX", code: "00" },
  { to: "/portfolio", label: "REELS", code: "01" },
  { to: "/gallery", label: "STILLS", code: "02" },
  { to: "/about", label: "DOSSIER", code: "03" },
  { to: "/onboarding", label: "BRIEF", code: "04" },
  { to: "/book", label: "BOOK", code: "05" },
];

// TikTok glyph (lucide doesn't have one — minimal inline)
const TikTokIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.39a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.82z"/></svg>
);

export default function Layout() {
  const loc = useLocation();
  const cfg = useConfig();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="cinema-bar top-0 h-2 bg-black" />
      <div className="fixed top-2 left-0 right-0 h-px bg-neutral-900 z-40 pointer-events-none" />

      <HUD />

      <header
        data-testid="site-header"
        className={`fixed top-2 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-black/85 backdrop-blur-md border-b border-neutral-900" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-baseline gap-3 group">
            <div className="relative">
              <span className="font-bodoni text-3xl tracking-tight">JITSUENT</span>
              <span className="absolute -top-1 -right-3 w-1.5 h-1.5 bg-white rounded-full flicker" />
            </div>
            <span className="font-mono text-[9px] tracking-[0.3em] text-neutral-600 hidden sm:inline">
              /MEDIA-OPS/v1
            </span>
          </Link>

          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-0 border border-neutral-900 bg-black/40 backdrop-blur">
              {NAV.map((n) => {
                const active = loc.pathname === n.to;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className={`group px-4 py-2 font-oswald text-[11px] tracking-[0.25em] uppercase relative border-r border-neutral-900 last:border-r-0 ${
                      active ? "text-white bg-neutral-950" : "text-neutral-500 hover:text-white hover:bg-neutral-950"
                    } transition-colors`}
                  >
                    <span className="text-neutral-700 mr-1.5 font-mono">{n.code}</span>
                    {n.label}
                    {active && (
                      <motion.span
                        layoutId="nav-indicator"
                        className="absolute -bottom-px left-2 right-2 h-px bg-white"
                      />
                    )}
                  </Link>
                );
              })}
            </div>
            <a
              href={cfg.shop_url || "https://jointsnjitsu.com"}
              target="_blank"
              rel="noreferrer"
              data-testid="shop-link"
              className="ml-3 btn-ghost text-[10px] !py-2 !px-3"
            >
              <ShoppingBag size={13} /> SHOP
            </a>
            <Link to="/onboarding" data-testid="cta-transmit" className="ml-2 btn-noir text-[10px] !py-2 !px-4">
              Transmit →
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-30 bg-black pt-24 overflow-y-auto lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-6 gap-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="flex items-baseline gap-4 py-3 border-b border-neutral-900"
                >
                  <span className="font-mono text-xs text-neutral-600">{n.code}</span>
                  <span className="font-bodoni text-3xl">{n.label}</span>
                </Link>
              ))}
              <a href={cfg.shop_url} target="_blank" rel="noreferrer" className="btn-ghost mt-4 justify-center">
                <ShoppingBag size={14} /> Shop @ Joints & Jitsu
              </a>
              <Link to="/onboarding" className="btn-noir justify-center">
                Begin Transmission →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="pt-24">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-neutral-900 mt-32 bg-black relative">
        <div className="absolute top-0 left-0 right-0 h-px chrome-border" />
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <div className="font-bodoni text-7xl mb-4 chrome-text">JITSUENT</div>
              <p className="font-mono text-xs text-neutral-500 leading-relaxed max-w-sm">
                Creative Fight Entertainment.  Media & Production house of Joints & Jitsu.
                Art. Science. Jiu-Jitsu. Captured in monochrome at the speed of light.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <a href={cfg.social?.instagram || "#"} target="_blank" rel="noreferrer" data-testid="social-instagram" className="w-10 h-10 border border-neutral-800 hover:border-white flex items-center justify-center text-neutral-500 hover:text-white transition-colors">
                  <Instagram size={14} />
                </a>
                <a href={cfg.social?.youtube || "#"} target="_blank" rel="noreferrer" data-testid="social-youtube" className="w-10 h-10 border border-neutral-800 hover:border-white flex items-center justify-center text-neutral-500 hover:text-white transition-colors">
                  <Youtube size={14} />
                </a>
                <a href={cfg.social?.tiktok || "#"} target="_blank" rel="noreferrer" data-testid="social-tiktok" className="w-10 h-10 border border-neutral-800 hover:border-white flex items-center justify-center text-neutral-500 hover:text-white transition-colors">
                  <TikTokIcon size={14} />
                </a>
                <a href={cfg.shop_url || "https://jointsnjitsu.com"} target="_blank" rel="noreferrer" data-testid="social-shop" className="ml-2 px-4 h-10 border border-neutral-800 hover:border-white flex items-center gap-2 text-neutral-500 hover:text-white text-[10px] tracking-[0.25em] font-oswald transition-colors">
                  <ShoppingBag size={12} /> SHOP
                </a>
              </div>
              <div className="mt-8 font-mono text-[10px] text-neutral-700 tracking-[0.3em]">
                @JOINTSNJITSU · 4EVER ROLLIN' · JOINT OSS
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-[10px] text-neutral-600 tracking-[0.3em] mb-4">NAVIGATE</div>
              <ul className="space-y-2">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="font-oswald text-sm text-neutral-400 hover:text-white link-underline tracking-widest">
                      <span className="text-neutral-700 mr-2 font-mono text-[10px]">{n.code}</span>{n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <div className="font-mono text-[10px] text-neutral-600 tracking-[0.3em] mb-4">FAMILY</div>
              <a href={cfg.shop_url || "https://jointsnjitsu.com"} target="_blank" rel="noreferrer" className="font-bodoni text-2xl link-underline block mb-2">
                jointsnjitsu.com →
              </a>
              <a href="/book" className="font-bodoni italic text-xl text-neutral-400 link-underline block mb-2">
                Book a session →
              </a>
              <Link to="/admin/login" data-testid="admin-link" className="font-mono text-[10px] text-neutral-700 hover:text-neutral-400 mt-8 inline-block tracking-[0.3em]">
                CREW ACCESS
              </Link>
            </div>
          </div>

          <div className="mt-16 pt-6 border-t border-neutral-900 grid grid-cols-1 md:grid-cols-3 gap-2 font-mono text-[10px] text-neutral-700 tracking-[0.3em]">
            <div>© {new Date().getFullYear()} JITSUENT</div>
            <div className="text-center">CALLSIGN: BONSAI-01</div>
            <div className="md:text-right">SIGNAL.STABLE · WARP.ENGAGED · ROLL.READY</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
